import React from 'react';
import { Paper, Box, Typography } from '@mui/material';

// Semantic accent tones — used sparingly (an icon chip + a left rule) rather than
// flooding the whole tile with saturated color, so a row of stats reads as one
// cohesive set instead of a rainbow of unrelated blocks.
const TONES = {
  neutral: { accent: '#1565c0', tint: '#eaf1fc' },
  positive: { accent: '#2e7d32', tint: '#e9f5ea' },
  caution: { accent: '#c77700', tint: '#fdf1e2' },
  critical: { accent: '#c62828', tint: '#fbeaea' },
  info: { accent: '#6a1b9a', tint: '#f2e9f7' },
};

/**
 * A compact stat tile: label, value, optional icon and sub-caption.
 * `tone` picks a restrained accent from TONES (defaults to 'neutral').
 */
const StatCard = ({ label, value, icon, caption, tone = 'neutral', sx = {} }) => {
  const { accent, tint } = TONES[tone] || TONES.neutral;
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        height: '100%',
        borderLeft: '3px solid',
        borderLeftColor: accent,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1.5,
        ...sx,
      }}
    >
      <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
            display: 'block',
            overflowWrap: 'break-word',
          }}
        >
          {label}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.25, lineHeight: 1.2 }}>
          {value}
        </Typography>
        {caption && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {caption}
          </Typography>
        )}
      </Box>
      {icon && (
        <Box
          sx={{
            width: 36, height: 36, borderRadius: '50%',
            bgcolor: tint, color: accent,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      )}
    </Paper>
  );
};

export default StatCard;
